import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import { debounce, set, take, tail } from "lodash";
import * as React from "react";
import Avatar from "react-avatar";
import Files from "react-files";
import {
    Button,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Pagination,
    Popover,
    Table,
} from "reactstrap";
import * as AccountStore from "../account/AccountStore";
import { GetContext, UnsubscribeContext } from "../general/ApplicationContext";
import * as IdentityPermissionStore from "../identityPermissions/IdentityPermissionStore";
import OrganizationContextSwitcher from "../organizations/ContextSwitcher";
import { MembershipType } from "../organizations/OrganizationStore";
import * as UserStore from "./UserStore";
import { TrimFields } from "../general/HttpClient";

const { Component } = React;

let contextSubscriptionId = "";

const UpdatedField = ({ oldValue, newValue }) => {
    return oldValue !== newValue ? (
        <>
            <del className="text-muted">{oldValue}</del>
            <span className="text-warning">{newValue}</span>
        </>
    ) : (
        <span>{newValue}</span>
    );
};

export const UsersIndex = () => {
    const [users, setUsers] = React.useState([] as any[]);
    const [skip, setSkip] = React.useState(0);
    const [limit, setLimit] = React.useState(25);
    const [searchQuery, setSearchQuery] = React.useState("");
    const [selectedUser, setSelectedUser] = React.useState({} as AccountStore.Account);
    const [showRawMetadata, setShowRawMetadata] = React.useState(false);
    const [selectedUserMetadata, setSelectedUserMetadata] = React.useState({});

    const [canViewMetadata, setCanViewMetadata] = React.useState(false);
    const [canImportUsers, setCanImportUsers] = React.useState(false);
    const [canCreateUsers, setCanCreateUsers] = React.useState(false);
    const [canImpersonate, setCanImpersonate] = React.useState(false);
    const [canSendMessage, setCanSendMessage] = React.useState(false);
    const [isSuperAdmin, setIsSuperAdmin] = React.useState(false);
    const [canEditUser, setCanEditUser] = React.useState(false);

    const [userFileUploaded, setUserFileUploaded] = React.useState(false);
    const [userFileName, setUserFileName] = React.useState("");
    const [excelFileData, setExcelFileData] = React.useState("");
    const [organizationId, setOrganizationId] = React.useState("");
    const [importUsers, setImportUsers] = React.useState([] as any[]);
    const [enableConfirmImport, setEnableConfirmImport] = React.useState(false);
    const [uploadError, setUploadError] = React.useState("");
    const [importError, setImportError] = React.useState("");
    const [canResetPassword, setCanResetPassword] = React.useState(false);
    const [newPassword, setNewPassword] = React.useState("");
    const [passwordErrors, setPasswordErrors] = React.useState([] as any[]);
    const [message, setMessage] = React.useState("");

    const [showMetadataModal, setShowMetadataModal] = React.useState(false);
    const [showImportUsersModal, setShowImportUsersModal] = React.useState(false);
    const [showResetPasswordModal, setShowResetPasswordModal] = React.useState(false);
    const [showImpersonateModal, setShowImpersonateModal] = React.useState(false);
    const [showUpdateUserModal, setShowUpdateUserModal] = React.useState(false);

    const [showCreateMessageModal, setShowCreateMessageModal] = React.useState(false);

    const [includeStaff, setIncludeStaff] = React.useState(true);
    const [includeStudents, setIncludeStudents] = React.useState(true);
    const [includeParents, setIncludeParents] = React.useState(true);
    const [includeGuests, setIncludeGuests] = React.useState(true);

    const showMetadata = (user) => () => {
        UserStore.GetUser(user.id).then((response) => {
            var userMetadata = {};

            response.data.metadata.forEach((m) => {
                if (m.enableOverride) {
                    set(userMetadata, m.key, m.overrideValue);
                } else {
                    set(userMetadata, m.key, m.value);
                }
            });

            setSelectedUser(response.data);
            setShowMetadataModal(true);
            setSelectedUserMetadata(userMetadata);
        });
    };

    const toggleMetadataModal = () => {
        setShowMetadataModal(!showMetadataModal);
    };

    const toggleImportUsersModal = () => {
        setUserFileName("");
        setUserFileUploaded(false);
        setImportUsers([]);
        setEnableConfirmImport(false);
        setShowImportUsersModal(!showImportUsersModal);
    };

    const searchUsers = (query, skip, limit) => {
        return UserStore.SearchUsers(query, skip, limit, includeStaff, includeStudents, includeParents, includeGuests).then((response) => {
            setUsers(response.data);
            return response;
        });
    };

    const debouncedSearchUsers = React.useMemo(() => debounce(searchUsers, 300), [searchQuery, includeStaff, includeStudents, includeParents, includeGuests]);

    React.useEffect(() => {
        contextSubscriptionId = GetContext((context) => {
            setOrganizationId(context.currentOrganization.id);
            getUsers();
            IdentityPermissionStore.CheckCurrentUserHasPermission("getUser").then((result) => {
                setCanViewMetadata(result);
            });
            IdentityPermissionStore.CheckCurrentUserHasPermission("importUsersFromExcel").then((result) => {
                setCanImportUsers(result);
            });
            IdentityPermissionStore.CheckCurrentUserHasPermission("createUser").then((result) => {
                setCanCreateUsers(result);
            });
            IdentityPermissionStore.CheckCurrentUserHasPermission("resetPassword").then((result) => {
                setCanResetPassword(result);
            });
            IdentityPermissionStore.CheckCurrentUserHasPermission("updateUser").then((result) => {
                setCanEditUser(result);
            });
            IdentityPermissionStore.CheckCurrentUserHasPermission("sendSingleMessage").then((result) => {
                setCanSendMessage(result);
            });
            IdentityPermissionStore.CheckCurrentUserHasPermission("impersonateUsers").then((result) => {
                AccountStore.GetCurrentLoggedInUser().then((response) => {
                    if (response.claims.some((c) => c.type === AccountStore.ROLE_CLAIM && c.value === "SuperAdmin")) {
                        setCanImpersonate(result);
                        setIsSuperAdmin(true);
                    }
                });
            });
        });
        return () => {
            UnsubscribeContext(contextSubscriptionId);
        };
    }, []);

    const getUsers = () => {
        UserStore.SearchUsers(searchQuery, skip, limit, includeStaff, includeStudents, includeParents, includeGuests)
            .then((result) => {
                setUsers(result.data);
            })
            .catch((response) => {
                setUsers([]);
            });
    };

    React.useEffect(() => {
        debouncedSearchUsers(searchQuery, 0, 25);
    }, [searchQuery, includeStaff, includeStudents, includeParents, includeGuests]);

    const handleQueryChange = (e) => {
        var query = e.target.value;
        setSearchQuery(query);
    };

    const onFilesChange = (files) => {
        var reader = new FileReader();

        reader.onload = (evt) => {
            let result = evt.target?.result?.toString();
            if (result) {
                setExcelFileData(result.substring(result.indexOf("base64,") + 7));
                setUserFileName(files[0].name);
                setUserFileUploaded(true);
            }
        };

        if (files.length > 0 && files[0]) {
            reader.readAsDataURL(files[0]);
        }
    };

    const onFilesError = (error, file) => {
        console.log("error code " + error.code + ": " + error.message);
    };

    const uploadUsers = () => {
        setImportUsers([]);
        UserStore.ImportUsersFromExcelTestRun(organizationId, excelFileData)
            .then((response) => {
                setUploadError("");
                setImportPage(0);
                setImportUsers(response.data);
                setEnableConfirmImport(true);
            })
            .catch((request) => {
                setUploadError(request.response.data);
            });
    };

    const doImportUsers = () => {
        UserStore.ImportUsersFromExcel(organizationId, excelFileData)
            .then((response) => {
                setShowImportUsersModal(false);
                setSearchQuery("");
                setEnableConfirmImport(false);
                setExcelFileData("");
                setUserFileName("");
                setUserFileUploaded(false);
                setImportUsers([]);
                setUploadError("");
                setImportError("");
                getUsers();
            })
            .catch((request) => {
                setImportError(request.response.data);
            });
    };

    const startResetPassword = (user) => () => {
        setShowResetPasswordModal(true);
        setSelectedUser(user);
    };

    const handlePasswordChange = (e) => {
        var value = e.target.value;
        setNewPassword(value);
    };

    const toggleResetPasswordModal = () => {
        setShowResetPasswordModal(!showResetPasswordModal);
    };

    const resetPassword = () => {
        UserStore.ResetPassword(selectedUser, newPassword)
            .then((response) => {
                if (response.data.isError) {
                    setPasswordErrors(response.data.errors);
                } else {
                    setSelectedUser({} as AccountStore.Account);
                    setNewPassword("");
                    setPasswordErrors([]);
                    setShowResetPasswordModal(false);
                }
            })
            .catch((errors) => {
                setPasswordErrors([{ description: "Password reset failed unexpectedly" }]);
            });
    };

    const startImpersonate = (user) => () => {
        setShowImpersonateModal(true);
        setSelectedUser(user);
    };

    const impersonate = () => {
        //@ts-ignore
        window.location = `/Account/Impersonate/$initiate?userId=${selectedUser.id}&organizationId=${organizationId}`;
    };

    const [importPage, setImportPage] = React.useState(0);

    const startSendMessage = (user) => () => {
        setShowCreateMessageModal(true);
        setSelectedUser(user);
    };

    const sendMessage = () => {
        axios
            .post(`/api/messaging/sendMessage`, {
                to: [selectedUser.id],
                text: message,
            })
            .then((response) => {
                setShowCreateMessageModal(false);
                if (response.data.success) {
                }
            });
    };

    const [showLoginLinkModal, setShowLoginLinkModal] = React.useState(false);
    const [loginLink, setLoginLink] = React.useState("");
    const [link, setLink] = React.useState(window.location.origin + "/");
    const [minutes, setMinutes] = React.useState(15);

    const startLoginLink = (user) => () => {
        setShowLoginLinkModal(true);
        setSelectedUser(user);
    };

    const createLoginLink = () => {
        axios
            .post("/Account/CreateLoginLinkForUser", {
                link: link,
                userID: selectedUser.id,
                organizationID: organizationId,
                minutes: minutes,
            })
            .then((response) => {
                setLoginLink(response.data);
                copyLink(response.data)();
            });
    };

    const copyLink = (url) => () => {
        // @ts-ignore
        navigator.permissions.query({ name: "clipboard-write" }).then((result) => {
            if (result.state == "granted" || result.state == "prompt") {
                navigator.clipboard.writeText(url).then(
                    function () {
                        /* clipboard successfully set */
                    },
                    function (e) {
                        console.log(e);
                        /* clipboard write failed */
                    }
                );
            }
        });
    };

    const [showCreateUserModal, setShowCreateUserModal] = React.useState(false);

    const [newUserEmail, setNewUserEmail] = React.useState("");
    const [newUserFirstName, setNewUserFirstName] = React.useState("");
    const [newUserLastName, setNewUserLastName] = React.useState("");
    const [newUserType, setNewUserType] = React.useState(0);
    const [newUserPhoneNumber, setNewUserPhoneNumber] = React.useState("");

    const toggleCreateUserModal = () => {
        setShowCreateUserModal(true);
    };

    const createUser = async () => {
        setSearchQuery("");
        let res = await UserStore.CreateUser({
            email: newUserEmail,
            firstName: newUserFirstName,
            lastName: newUserLastName,
            membershipType: newUserType,
            organization: organizationId,
        } as UserStore.CreateIdentityUserRequest);
        getUsers();
        setShowCreateUserModal(false);
    };

    const startUpdateUser = (user) => () => {
        setShowUpdateUserModal(true);
        setSelectedUser(user);
        setNewUserEmail(user.email);
        setNewUserFirstName(user.firstName);
        setNewUserLastName(user.lastName);
        setNewUserPhoneNumber(user.phoneNumber);
        setUpdateError("");
    };

    const [updateError, setUpdateError] = React.useState("");

    const updateUser = async () => {
        let res = await UserStore.UpdateUser(
            TrimFields({
                id: selectedUser.id,
                email: newUserEmail,
                firstName: newUserFirstName,
                lastName: newUserLastName,
                phoneNumber: newUserPhoneNumber,
            } as UserStore.UserUpdateModel)
        ).catch((r) => r.response);
        if (res.statusText === "OK") {
            getUsers();
            setShowUpdateUserModal(false);
        } else {
            setUpdateError(res.data.message);
        }
    };

    const [showManageClaimsModal, setShowManageClaimsModal] = React.useState(false);
    const [selectedUserClaims, setSelectedUserClaims] = React.useState([] as any[]);

    const startManageClaims = (user) => () => {
        UserStore.GetUserClaims(user.id).then((response) => {
            setSelectedUserClaims(response.data);
            setShowManageClaimsModal(true);
            setSelectedUser(user);
        });
    };

    return (
        <div>
            <OrganizationContextSwitcher></OrganizationContextSwitcher>
            <div className="form-group row p-1">
                <div className="col-xs-12 col-sm-3">
                    <input type="text" className="form-control" name="search" value={searchQuery} onChange={handleQueryChange} placeholder="Search" />
                    <div style={{ marginTop: "0.5rem" }}>
                        <span>Search for </span>
                        <Button outline={!includeStaff} color="primary" onClick={() => setIncludeStaff(!includeStaff)} style={{}}>
                            Staff
                        </Button>
                        <Button outline={!includeStudents} color="primary" onClick={() => setIncludeStudents(!includeStudents)} style={{ marginLeft: "1rem" }}>
                            Students
                        </Button>
                        <Button outline={!includeParents} color="primary" onClick={() => setIncludeParents(!includeParents)} style={{ marginLeft: "1rem" }}>
                            Parents
                        </Button>
                        <Button outline={!includeGuests} color="primary" onClick={() => setIncludeGuests(!includeGuests)} style={{ marginLeft: "1rem" }}>
                            Guests
                        </Button>
                    </div>
                </div>
                <div className="col-xs-12 col-sm-9">
                    {canImportUsers && (
                        <Button outline color="dark" onClick={toggleImportUsersModal}>
                            <FontAwesomeIcon icon={["fad", "file-import"]}></FontAwesomeIcon> Import Users
                        </Button>
                    )}
                    {canCreateUsers && (
                        <Button outline color="dark" onClick={toggleCreateUserModal} style={{ marginLeft: "1rem" }}>
                            <FontAwesomeIcon icon={["fad", "user-plus"]}></FontAwesomeIcon> Create User
                        </Button>
                    )}
                </div>
            </div>
            <table className="table">
                <thead>
                    <tr>
                        <th style={{ width: "5%" }}></th>
                        <th style={{ width: "25%" }}>Name</th>
                        <th style={{ width: "25%" }}>Email</th>
                        <th style={{ width: "10%" }}>Phone Number</th>
                        <th style={{ width: "10%" }}>Source ID</th>
                        <th style={{ width: "25%" }}></th>
                    </tr>
                </thead>
                <tbody>
                    {users.map((user, i) => (
                        <tr key={i}>
                            <td>
                                <Avatar name={user.fullName} src={user.photo} size="50px" round={true} />
                            </td>
                            <td>
                                <div>{user.fullName}</div>
                                <small className="text-muted">{user.id}</small>
                            </td>
                            <td>
                                <div>{user.email || "-"}</div>
                            </td>
                            <td>{user.phoneNumber || "-"}</td>
                            <td>{user.sourceID || "-"}</td>
                            <td>
                                {canViewMetadata && (
                                    <Button color="primary" outline onClick={showMetadata(user)}>
                                        Metadata
                                    </Button>
                                )}
                                {canResetPassword && (
                                    <Button style={{ marginLeft: "1rem" }} outline onClick={startResetPassword(user)}>
                                        Reset Password
                                    </Button>
                                )}
                                {canEditUser && (
                                    <Button style={{ marginLeft: "1rem" }} outline onClick={startUpdateUser(user)}>
                                        Update User
                                    </Button>
                                )}
                                {/* {canImpersonate && (
                                    <Button color="danger" style={{ marginLeft: "1rem" }} outline onClick={startImpersonate(user)}>
                                        Impersonate
                                    </Button>
                                )} */}
                                {canSendMessage && (
                                    <Button color="success" style={{ marginLeft: "1rem" }} outline onClick={startSendMessage(user)}>
                                        Send Message
                                    </Button>
                                )}
                                {canImpersonate && (
                                    <Button color="success" style={{ marginLeft: "1rem" }} outline onClick={startLoginLink(user)}>
                                        Create Login Link
                                    </Button>
                                )}
                                {isSuperAdmin && (
                                    <Button color="success" style={{ marginLeft: "1rem" }} outline onClick={startManageClaims(user)}>
                                        Manage Claims
                                    </Button>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <Modal isOpen={showMetadataModal} toggle={toggleMetadataModal} centered={true}>
                <ModalHeader toggle={toggleMetadataModal}>Metadata</ModalHeader>
                <ModalBody>
                    <Label check className="pl-4">
                        <Input type="checkbox" onClick={() => setShowRawMetadata(!showRawMetadata)} /> Show Raw
                    </Label>
                    {showMetadataModal && showRawMetadata && (
                        <pre>
                            {selectedUser.metadata.map((m) => {
                                if (m.enableOverride) {
                                    return (
                                        <div>
                                            {m.key} : <span style={{ textDecoration: "line-through" }}>{m.value}</span> {m.overrideValue}
                                        </div>
                                    );
                                } else {
                                    return (
                                        <div>
                                            {m.key} {m.value}
                                        </div>
                                    );
                                }
                            })}
                        </pre>
                    )}
                    {showMetadataModal && !showRawMetadata && <pre>{JSON.stringify(selectedUserMetadata, null, 2)}</pre>}
                    <br />
                    <strong>Organization Membership</strong>
                    {showMetadataModal &&
                        selectedUser.organizationMembership.map((member, i) => (
                            <div key={"organizationMembership" + i}>
                                {member.organization.name} - {MembershipType(member.type)} <br />
                                <div className="text-muted">
                                    {member.source} - {member.sourceID}
                                </div>
                            </div>
                        ))}
                    <strong>Group Membership</strong>
                    {showMetadataModal && selectedUser.groups.map((group, i) => <div key={"groupMembership" + i}>{group.name}</div>)}
                </ModalBody>
                <ModalFooter>
                    <Button type="button" className="btn btn-secondary" onClick={toggleMetadataModal}>
                        Close
                    </Button>
                </ModalFooter>
            </Modal>

            <Modal isOpen={showImportUsersModal} toggle={toggleImportUsersModal} centered={true} size="lg">
                <ModalHeader toggle={toggleImportUsersModal}>Import Users</ModalHeader>
                <ModalBody>
                    {importUsers.length == 0 && (
                        <Files
                            className="files-dropzone"
                            onChange={onFilesChange}
                            onError={onFilesError}
                            accepts={[".xlsx"]}
                            maxFileSize={10000000}
                            minFileSize={0}
                            clickable
                            multiple={false}
                        >
                            <a href="#" className="p-2">
                                {userFileUploaded && userFileName}
                                {!userFileUploaded && "Drop file here or click to upload"}
                            </a>
                        </Files>
                    )}
                    {uploadError && <div className="text-danger">{uploadError}</div>}

                    {importUsers.length > 0 && (
                        <>
                            <table className="table">
                                <caption>Showing 25 results</caption>
                                <thead>
                                    <tr>
                                        <th style={{ width: "10%" }}></th>
                                        <th style={{ width: "40%" }}>Name</th>
                                        <th style={{ width: "20%" }}>Email</th>
                                        <th style={{ width: "10%" }}>Phone Number</th>
                                        <th style={{ width: "10%" }}>Source </th>
                                        <th style={{ width: "10%" }}>Source ID</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {importUsers
                                        .filter((_, i) => i >= 25 * importPage && i < 25 * (importPage + 1))
                                        .map(({ user, isNew, isUpdate, existingUser }, i) => (
                                            <tr key={i}>
                                                <td>
                                                    <div>{MembershipType(user.type)}</div>
                                                </td>
                                                <td>
                                                    {!isNew && <UpdatedField oldValue={existingUser.fullName} newValue={user?.fullName}></UpdatedField>}
                                                    {isNew && <span className="text-success">{user?.fullName}</span>}
                                                </td>
                                                <td>
                                                    {!isNew && <UpdatedField oldValue={existingUser.email} newValue={user?.email}></UpdatedField>}
                                                    {isNew && <span className="text-success">{user?.email}</span>}
                                                </td>
                                                <td>
                                                    {!isNew && <UpdatedField oldValue={existingUser.phoneNumber} newValue={user?.phoneNumber}></UpdatedField>}
                                                    {isNew && <span className="text-success">{user?.phoneNumber}</span>}
                                                </td>
                                                <td>
                                                    {!isNew && <UpdatedField oldValue={existingUser.source} newValue={user?.source}></UpdatedField>}
                                                    {isNew && <span className="text-success">{user?.source}</span>}
                                                </td>
                                                <td>
                                                    {!isNew && <UpdatedField oldValue={existingUser.sourceID} newValue={user?.sourceID}></UpdatedField>}
                                                    {isNew && <span className="text-success">{user?.sourceID}</span>}
                                                </td>
                                                <td>
                                                    {isUpdate && <span className="text-warning">Updated</span>}
                                                    {isNew && <span className="text-success">New</span>}
                                                </td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                            <Button onClick={() => setImportPage(Math.max(importPage - 1, 0))}>Previous Page</Button>
                            <Button onClick={() => setImportPage(Math.min(importPage + 1, Math.floor(importUsers.length / 25)))}>Next Page</Button>
                        </>
                    )}

                    {importError && <div className="text-danger">{importError}</div>}
                </ModalBody>
                <ModalFooter>
                    <div className="d-flex justify-content-between w-100">
                        <a href="api/integrations/downloadUserExcelImportTemplate" download="User Import Template.xlsx">
                            Download Template
                        </a>
                        <div>
                            <Button type="button" color="secondary" onClick={toggleImportUsersModal}>
                                Cancel
                            </Button>
                            {userFileUploaded && !enableConfirmImport && (
                                <Button style={{ marginLeft: "1rem" }} onClick={uploadUsers} color="primary" outline>
                                    Next
                                </Button>
                            )}
                            {enableConfirmImport && (
                                <Button style={{ marginLeft: "1rem" }} type="button" color="primary" onClick={doImportUsers}>
                                    Confirm Import
                                </Button>
                            )}
                        </div>
                    </div>
                </ModalFooter>
            </Modal>

            <Modal isOpen={showCreateUserModal} toggle={() => setShowCreateUserModal(!showCreateUserModal)} centered={true}>
                <ModalHeader toggle={() => setShowCreateUserModal(!showCreateUserModal)}>Create User</ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label>Email</Label>
                        <Input type="email" value={newUserEmail} onChange={(e) => setNewUserEmail(e.target.value)}></Input>
                    </FormGroup>
                    <FormGroup>
                        <Label>First Name</Label>
                        <Input type="text" value={newUserFirstName} onChange={(e) => setNewUserFirstName(e.target.value)}></Input>
                    </FormGroup>
                    <FormGroup>
                        <Label>Last Name</Label>
                        <Input type="text" value={newUserLastName} onChange={(e) => setNewUserLastName(e.target.value)}></Input>
                    </FormGroup>
                    <FormGroup>
                        <Label>Phone Number</Label>
                        <Input type="text" value={newUserPhoneNumber} onChange={(e) => setNewUserPhoneNumber(e.target.value)}></Input>
                    </FormGroup>
                    <FormGroup>
                        <Label>Type</Label>
                        <Input type="select" name="type" value={newUserType} onChange={(e) => setNewUserType(e.target.valueAsNumber)}>
                            <option value="0">Staff</option>
                            <option value="1">Student</option>
                            <option value="2">Parent</option>
                            <option value="3">Guest</option>
                        </Input>
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button type="button" color="secondary" onClick={() => setShowCreateUserModal(false)}>
                        Cancel
                    </Button>
                    <Button type="button" color="success" disabled={!newUserEmail} onClick={createUser}>
                        Create
                    </Button>
                </ModalFooter>
            </Modal>

            <Modal isOpen={showUpdateUserModal} toggle={() => setShowUpdateUserModal(!showUpdateUserModal)} centered={true}>
                <ModalHeader toggle={() => setShowUpdateUserModal(!showUpdateUserModal)}>Update User</ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label>Email</Label>
                        <Input type="email" value={newUserEmail} onChange={(e) => setNewUserEmail(e.target.value)}></Input>
                    </FormGroup>
                    <FormGroup>
                        <Label>First Name</Label>
                        <Input type="text" value={newUserFirstName} onChange={(e) => setNewUserFirstName(e.target.value)}></Input>
                    </FormGroup>
                    <FormGroup>
                        <Label>Last Name</Label>
                        <Input type="text" value={newUserLastName} onChange={(e) => setNewUserLastName(e.target.value)}></Input>
                    </FormGroup>
                    <FormGroup>
                        <Label>Phone Number</Label>
                        <Input type="text" value={newUserPhoneNumber} onChange={(e) => setNewUserPhoneNumber(e.target.value)}></Input>
                    </FormGroup>
                    <div className="text-danger">{updateError}</div>
                </ModalBody>
                <ModalFooter>
                    <Button type="button" color="secondary" onClick={() => setShowUpdateUserModal(false)}>
                        Cancel
                    </Button>
                    <Button type="button" color="success" disabled={!newUserEmail} onClick={updateUser}>
                        Update
                    </Button>
                </ModalFooter>
            </Modal>

            <Modal isOpen={showResetPasswordModal} toggle={toggleResetPasswordModal} centered={true}>
                <ModalHeader toggle={toggleResetPasswordModal}>Reset Password</ModalHeader>
                <ModalBody>
                    <div>
                        Change password for {selectedUser.firstName} {selectedUser.lastName}
                    </div>
                    <FormGroup>
                        <Label>New Password</Label>
                        <Input type="text" onChange={handlePasswordChange} value={newPassword}></Input>
                    </FormGroup>

                    {passwordErrors.length > 0 &&
                        passwordErrors.map((p, i) => (
                            <div className="text-danger" key={i}>
                                {p.description}
                            </div>
                        ))}
                </ModalBody>
                <ModalFooter>
                    <Button type="button" color="secondary" onClick={toggleResetPasswordModal}>
                        Cancel
                    </Button>
                    <Button type="button" color="primary" onClick={resetPassword}>
                        Reset Password
                    </Button>
                </ModalFooter>
            </Modal>

            {/* <Modal isOpen={showImpersonateModal} toggle={() => setShowImpersonateModal(!showImpersonateModal)} centered={true}>
                <ModalHeader toggle={() => setShowImpersonateModal(!showImpersonateModal)}>Impersonate User</ModalHeader>
                <ModalBody>
                    <div className="alert alert-danger text-center">
                        <strong>!!WARNING!!</strong>
                    </div>

                    <h4 className="text-center">
                        You are about to be signed in as {selectedUser.firstName} {selectedUser.lastName}
                    </h4>

                    <div className="alert alert-danger text-center">
                        <strong>!!WARNING!!</strong>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button type="button" color="secondary" onClick={() => setShowImpersonateModal(!false)}>
                        Cancel
                    </Button>
                    <Button type="button" color="danger" onClick={impersonate}>
                        Impersonate
                    </Button>
                </ModalFooter>
            </Modal> */}

            <Modal isOpen={showCreateMessageModal} toggle={() => setShowCreateMessageModal(!showCreateMessageModal)} centered={true}>
                <ModalHeader toggle={() => setShowCreateMessageModal(!showCreateMessageModal)}>Send Message</ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Input type="textarea" value={message} onChange={(e) => setMessage(e.target.value)}></Input>
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button type="button" color="secondary" onClick={() => setShowCreateMessageModal(false)}>
                        Cancel
                    </Button>
                    <Button type="button" color="success" disabled={!message} onClick={sendMessage}>
                        Send
                    </Button>
                </ModalFooter>
            </Modal>

            <Modal isOpen={showLoginLinkModal} toggle={() => setShowLoginLinkModal(!showLoginLinkModal)} centered={true}>
                <ModalHeader toggle={() => setShowLoginLinkModal(!showLoginLinkModal)}>Create Login Link</ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label>URL</Label>
                        <Input type="text" value={link} onChange={(e) => setLink(e.target.value)}></Input>
                    </FormGroup>
                    <FormGroup>
                        <Label>Expiry (minutes from now)</Label>
                        <Input type="number" value={minutes} onChange={(e) => setMinutes(e.target.valueAsNumber)}></Input>
                    </FormGroup>
                    <Button type="button" color="primary" onClick={createLoginLink}>
                        Create
                    </Button>
                    {loginLink && <>Copied to clipboard</>}
                </ModalBody>
                <ModalFooter>
                    <Button type="button" color="secondary" onClick={() => setShowLoginLinkModal(false)}>
                        Close
                    </Button>
                </ModalFooter>
            </Modal>

            <Modal isOpen={showManageClaimsModal} toggle={() => setShowManageClaimsModal(!showManageClaimsModal)} centered={true}>
                <ModalHeader toggle={() => setShowManageClaimsModal(!showManageClaimsModal)}>Manage Claims</ModalHeader>
                <ModalBody>
                    <div className="form-group">
                        <label>Claims</label>
                        <Table>
                            <thead>
                                <tr>
                                    <th>Claim Type</th>
                                    <th>Claim Value</th>
                                </tr>
                            </thead>
                            <tbody>
                                {selectedUserClaims.map((claim, i) => (
                                    <tr key={i}>
                                        <td title={claim.type} style={{ maxWidth: "150px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                            {claim.type}
                                        </td>
                                        <td title={claim.value} style={{ maxWidth: "250px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                            {claim.value}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>

                    <Button
                        onClick={() => {
                            UserStore.AddClaim(selectedUser.id, "http://schemas.microsoft.com/ws/2008/06/identity/claims/role", "User").then(() => {
                                UserStore.GetUserClaims(selectedUser.id).then((response) => {
                                    setSelectedUserClaims(response.data);
                                });
                            });
                        }}
                    >
                        Add User Claim
                    </Button>
                    <Button
                        onClick={() => {
                            UserStore.RemoveClaim(selectedUser.id, "http://schemas.microsoft.com/ws/2008/06/identity/claims/role", "User").then(() => {
                                UserStore.GetUserClaims(selectedUser.id).then((response) => {
                                    setSelectedUserClaims(response.data);
                                });
                            });
                        }}
                    >
                        Remove User Claim
                    </Button>
                </ModalBody>
                <ModalFooter>
                    <Button type="button" color="secondary" onClick={() => setShowManageClaimsModal(false)}>
                        Close
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};
